import { useState } from "react";
import {
  Table,
  Tag,
  Pagination,
  Card,
  DatePicker,
  Select,
  Button,
  Spin,
} from "antd";
import { FilterOutlined } from "@ant-design/icons";
import SearchInput from "../../../components/common/SearchInput";
import NoData from "../../../components/common/NoData";
import moment from "moment";
import { useGetMessageLogs } from "../../../hooks/api/notification/notificationService";
import dayjs from "dayjs";
const { RangePicker } = DatePicker;
const { Option } = Select;

const MessageLogs = () => {
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    search: "",
    channel: "",
    status: "",
    startDate: "",
    endDate: "",
  });

  // Use the React Query hook to fetch message logs
  const { data: logsResponse, isLoading, error } = useGetMessageLogs(params);

  // Extract logs and pagination from response based on the actual API structure
  const logs = logsResponse?.data?.messageLogs || [];
  const pagination = logsResponse?.data?.pagination;

  const searchProps = {
    setSearchTerm: (searchTerm: string) => {
      setParams((prev) => ({ ...prev, search: searchTerm, page: 1 }));
    },
    setPageNumber: (pageNumber: number) => {
      setParams((prev) => ({ ...prev, page: pageNumber }));
    },
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setParams((prev) => ({
      ...prev,
      page,
      limit: pageSize,
    }));
  };

  const handleDateChange = (dates: any) => {
    setParams((prev) => ({
      ...prev,
      startDate: dates?.[0]?.format("YYYY-MM-DD") || "",
      endDate: dates?.[1]?.format("YYYY-MM-DD") || "",
      page: 1,
    }));
  };

  const handleFilterChange = (field: string, value: string) => {
    setParams((prev) => ({ ...prev, [field]: value, page: 1 }));
  };

  const clearFilters = () => {
    setParams({
      page: 1,
      limit: 10,
      search: "",
      channel: "",
      status: "",
      startDate: "",
      endDate: "",
    });
  };

  // Helper function to extract numeric value from cost string
  const extractCost = (costString: string) => {
    if (!costString) return 0;
    const num = parseFloat(costString.replace(/[^0-9.]/g, ""));
    return isNaN(num) ? 0 : num;
  };

  const columns = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Channel",
      dataIndex: "channel",
      key: "channel",
      render: (channel: string) => (
        <Tag color={channel === "SMS" ? "blue" : "green"}>{channel}</Tag>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      ellipsis: true,
      width: 300,
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
      render: (trigger: string) => trigger || "Manual",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        let color = "blue";
        if (status === "delivered") color = "green";
        if (status === "failed") color = "red";
        if (status === "read") color = "purple";

        return (
          <Tag color={color}>
            {status?.charAt(0).toUpperCase() + status?.slice(1)}
          </Tag>
        );
      },
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => cost || "$0.00",
      sorter: (a: any, b: any) => extractCost(a.cost) - extractCost(b.cost),
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp: string) =>
        moment(timestamp).format("DD/MM/YYYY HH:mm:ss"),
      sorter: (a: any, b: any) =>
        moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf(),
    },
  ];

  if (error) {
    return (
      <div className="mt-6">
        <div className="h-container">
          <Card>
            <div className="text-center text-red-500 py-8">
              Failed to load message logs. Please try again.
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="h-container">
        <div className="flex flex-wrap lg:flex-nowrap gap-x-72 items-center justify-between mb-4">
          <div className="mb-2 w-full">
            <h3 className="chart-heading">Message Logs</h3>
            <p className="chart-para">All sent SMS and WhatsApp messages</p>
          </div>
          <div className="flex items-center gap-x-3">
            <div className="lg:w-[340px]">
              <SearchInput
                {...searchProps}
                placeholder="Search by user, message, trigger"
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card size="small" className="mb-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <FilterOutlined />
              <span className="text-sm font-medium">Filters:</span>
            </div>

            <Select
              placeholder="Channel"
              style={{ width: 120 }}
              value={params.channel || undefined}
              onChange={(value) => handleFilterChange("channel", value)}
              allowClear
            >
              <Option value="SMS">SMS</Option>
              {/* <Option value="WHATSAPP">WhatsApp</Option> */}
            </Select>

            <Select
              placeholder="Status"
              style={{ width: 120 }}
              value={params.status || undefined}
              onChange={(value) => handleFilterChange("status", value)}
              allowClear
            >
              <Option value="delivered">Delivered</Option>
              <Option value="read">Read</Option>
              <Option value="failed">Failed</Option>
              <Option value="sent">Sent</Option>
            </Select>

            <RangePicker
              onChange={handleDateChange}
              value={[
                params.startDate ? dayjs(params.startDate) : null,
                params.endDate ? dayjs(params.endDate) : null,
              ]}
            />

            <Button onClick={clearFilters}>Clear Filters</Button>
          </div>
        </Card>

        <Card>
          <Spin spinning={isLoading}>
            <Table
              columns={columns}
              dataSource={logs}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: <NoData header="No Message Logs Available" />,
              }}
              scroll={{ x: 1000 }}
            />

            {pagination && pagination.total > params.limit && (
              <div className="flex mt-6 justify-end">
                <Pagination
                  current={params.page}
                  pageSize={params.limit}
                  total={pagination.total}
                  onChange={handlePageChange}
                  showSizeChanger
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`
                  }
                />
              </div>
            )}
          </Spin>
        </Card>
      </div>
    </div>
  );
};

export default MessageLogs;
